import React, { useState } from "react";
import { Company } from "../types";

type Props = {
  apiKey: string;
  onSelect: (partial: Partial<Company>) => void;
  onClose: () => void;
};

const buildAddress = (r: any) =>
  [
    r.adresseEtablissement?.numeroVoieEtablissement,
    r.adresseEtablissement?.indiceRepetitionEtablissement,
    r.adresseEtablissement?.typeVoieEtablissement,
    r.adresseEtablissement?.libelleVoieEtablissement,
  ]
    .filter(Boolean)
    .join(" ");

const escapeLucene = (str: string) =>
  str.replace(/([+\-!(){}\[\]^"~*?:\\/])/g, "\\$1");

// 🔠 Table des formes juridiques connues
const legalForms: Record<string, string> = {
    "1000": "Entrepreneur individuel",
    "1010": "EI",
    "5485": "EURL",
    "5599": "SA à conseil d'administration (s.a.i.)",
    "5710": "SAS",
    "5720": "SAS",
    "5498": "SARL",
    "5499": "EURL",
    "5400": "SA",
    "6316": "CUMA",
    "6530": "SNC",
    "6534": "GFA",
    "6598": "EARL",
    "9220": "Association",
    "9305": "Microentreprise",
    "7310": "SCOP",
    "7311": "SCIC",
    "7340": "SCE", // ⚠️ Attention : ici 7340 pour "Société coopérative européenne"
    "6599": "Personne morale de droit privé",
    "6597": "SCEA", // ✅ Société Civile d'Exploitation Agricole
    "3205": "GIE",
    "7210": "Profession libérale",
    "7215": "Indépendant",
  };  

const InseeCompanySearch: React.FC<Props> = ({ apiKey, onSelect, onClose }) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 5;
  const totalPages = Math.ceil(results.length / pageSize);
  const paginatedResults = results.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSearch = async () => {
    const cleaned = query.trim().replace(/\s+/g, " ");
    if (!cleaned) return;

    const terms = cleaned.split(" ").map((word) => `raisonSociale:${escapeLucene(word)}*`);
    const luceneQuery = terms.join(" AND ");

    setLoading(true);
    setError(null);
    setResults([]);
    setCurrentPage(1);

    try {
      const params = new URLSearchParams({
        q: luceneQuery,
        nombre: "50",
      });

      const url = `https://api.insee.fr/api-sirene/3.11/siret?${params.toString()}`;

      const resp = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-INSEE-Api-Key-Integration": apiKey,
        },
      });

      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(`Erreur HTTP ${resp.status} : ${resp.statusText} — ${errText}`);
      }

      const data = await resp.json();
      if (!Array.isArray(data.etablissements)) {
        throw new Error("Réponse inattendue de l'API INSEE");
      }

      // On filtre uniquement les établissements dont la dernière période est "A" (actif)
      const actifs = data.etablissements.filter((etab: any) => {
        const lastPeriod = etab.periodesEtablissement?.[0];
        return lastPeriod?.etatAdministratifEtablissement === "A";
      });

      setResults(actifs);
    } catch (err: any) {
      console.error("Erreur INSEE :", err);
      setError("Erreur lors de la recherche. Vérifiez la clé API ou la connexion.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="insee-overlay">
      <div className="insee-box">
        <header className="insee-toolbar">
          <h3>Recherche d'une société via le SIREN</h3>
          <button onClick={onClose}>✕</button>
        </header>

        <div className="insee-body">
          <input
            autoFocus
            type="text"
            placeholder="Saisir un nom de société"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="insee-input"
          />
          <button onClick={handleSearch} disabled={loading} className="insee-search-button">
            Rechercher
          </button>

          {loading && <p>Recherche en cours…</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}

          {results.length > 0 && (
            <div className="insee-results-box">
              <p>Résultats (page {currentPage} sur {totalPages}) :</p>
              <ul className="insee-results-list">
                {paginatedResults.map((r) => {
                  const siret = r.siret;
                  const legalUnit = r.uniteLegale;
                  const legalCode = legalUnit?.categorieJuridiqueUniteLegale || "";
                  const legalLabel = legalForms[legalCode] || "Structure inconnue";
                  const city = r.adresseEtablissement?.libelleCommuneEtablissement || "";

                  const denomination =
                    legalUnit?.denominationUniteLegale ||
                    (legalUnit?.prenom1UniteLegale && legalUnit?.nomUniteLegale
                      ? `${legalUnit.prenom1UniteLegale} ${legalUnit.nomUniteLegale}`
                      : "Nom inconnu");

                  return (
                    <li key={siret} className="insee-result-item">
                      <button
                        className="insee-result-button"
                        onClick={() =>
                          onSelect({
                            siret,
                            name: denomination,
                            address: buildAddress(r),
                            postalCode: r.adresseEtablissement?.codePostalEtablissement || "",
                            city,
                            country: "France",
                          })
                        }
                      >
                        {denomination}, {legalLabel}, {city}
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="pagination">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Précédent
                </button>
                <span>Page {currentPage} / {totalPages}</span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}

          {!loading && results.length === 0 && !error && query.trim() && (
            <p style={{ fontStyle: "italic" }}>Aucun résultat trouvé.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InseeCompanySearch;